import React, {
    Fragment,
    useEffect,
    useContext,
    useState,
    useCallback,
} from "react";
import axios from "axios";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import fileDownload from "js-file-download";
import { FaSync } from "react-icons/fa";

import SubHeader from "../Layout/SubHeader";
import OrdersTable from "../Orders/OrdersTable";
import InfoModal from "../Layout/InfoModal";
import BatchAssignationsModal from "./BatchAssignationsModal";

import { SiteContext } from "../../context/SiteContext";
import { UserContext } from "../../context/UserContext";

import useSortableData from "../../hooks/SortableData";

import { filterDateToQuery, filterGroupToQuery, chunks } from "../../helpers/Query";
import { sortSeletedOrdersFromSortedOrders } from "../../helpers/SortableOrders";
import { getOrderWithStoresFiltersSchema } from "../../helpers/Filters";

const Pickings = () => {
    const { env, csrfToken, salesShopGroup } = useContext(SiteContext);
    const { user } = useContext(UserContext);

    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [lastLoadTime, setLastLoadTime] = useState(new Date());

    const [selectedOrders, setSelectedOrders] = useState([]);
    const [printingPicking, setPrintingPicking] = useState(false);

    const [infoModalData, setInfoModalData] = useState(null);
    const [itemQuantity, setItemQuantity] = useState(0);

    const [batchAssignation, setBatchAssignation] = useState(false);

    const [orderFilters, setOrderFilters] = useState({
        ...getOrderWithStoresFiltersSchema(salesShopGroup),
        statuses: [
            { id: "pending", name: "pending", value: true },
            { id: "picking", name: "picking", value: false },
        ],
    });

    const { items, requestSort, sortConfig } = useSortableData(orders, {
        direction: "ascending",
        key: "location",
    });

    const orderFields = [
        { name: "Tienda", id: "shop" },
        { name: "Número", id: "idShop" },
        { name: "Cliente", id: "clientName" },
        { name: "Fecha creación", id: "createdAt" },
        { name: "Fecha update", id: "updatedAt" },
        { name: "Tipo envío", id: "shippingType" },
        { name: "Estado", id: "status" },
        { name: "Marca", id: "brand" },
        { name: "Ubic.", id: "location" },
        { name: "Ítems", id: "items" },
        { name: "Total", id: "totalAmount" },
        { name: "Boleta enviada", id: "invoiceSentAt" },
    ];

    const handleOrderSelect = (orderId, checked) => {
        //console.log("Cambia item ", orderId, " checked ", checked);

        if (checked) {
            setSelectedOrders((oldSelectedOrders) => {
                if (oldSelectedOrders.includes(orderId)) {
                    return oldSelectedOrders;
                } else {
                    return [...oldSelectedOrders, orderId];
                }
            });
        } else {
            setSelectedOrders((oldSelectedOrders) =>
                oldSelectedOrders.filter((id) => id !== orderId)
            );
        }
    };

    const fetchOrders = useCallback(() => {
        setOrdersLoading(true);

        let query = "?";
        query += filterGroupToQuery("status", orderFilters.statuses);
        query += filterGroupToQuery("brand", orderFilters.brands);
        query += filterGroupToQuery("shippingType", orderFilters.shippingTypes);
        query += filterDateToQuery("date", orderFilters.date);
        query += filterGroupToQuery("store", orderFilters.stores);
        query += filterGroupToQuery("floor", orderFilters.floors);
        query += filterGroupToQuery("assignedLocation", orderFilters.assignedLocations);
        query += filterGroupToQuery("shopGroups", orderFilters.shopGroups);

        axios
            .get(env.webApiBaseUrl + "/orders" + query, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-Token": csrfToken,
                    Authorization: "Bearer " + user.token,
                },
                withCredentials: true,
                timeout: 60000,
            })
            .then((ordersResponse) => {
                setOrders(ordersResponse.data);
                setSelectedOrders([]);
            })
            .catch((error) => {
                console.error(error);
                setInfoModalData({
                    type: "error",
                    msg: "No pude recuperar las ventas.",
                    closeButton: true,
                });
            })
            .finally(() => {
                setOrdersLoading(false);
                setLastLoadTime(new Date());
            });
    }, [env, csrfToken, user, setOrdersLoading, setOrders, orderFilters]);

    const handlePrintPickingsForChunk = async (selectedOrdersChunk) => {
        const pickingsRequest = {
            selectedOrders: selectedOrdersChunk,
        };

        console.log(
            "Printing picking tickets for: ",
            pickingsRequest.selectedOrders
        );

        try {
            const pickingsResponse = await axios.post(env.webApiBaseUrl + "/orders/pickings", pickingsRequest, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-Token": csrfToken,
                    Authorization: "Bearer " + user.token,
                },
                withCredentials: true,
                responseType: "blob",
                timeout: 300000,
            });

            const filename = "pickings-" + Date.now() + ".pdf";
            fileDownload(pickingsResponse.data, filename);

            setSelectedOrders((oldSelectedOrders) =>
                oldSelectedOrders.filter(
                    (order) => !selectedOrdersChunk.includes(order)
                )
            );
        } catch (e) {
            console.error("Problemas al generar pickings para grupo: ", e);
            throw e;
        }
    };

    const handlePrintPickings = async () => {
        setPrintingPicking(true);

        try {
            const selectedOrdersChunks = chunks(
                sortSeletedOrdersFromSortedOrders(selectedOrders, items),
                env.downloadMaxChunkSize
            );

            let chunkNumber = 1;

            for (const selectedOrdersChunk of selectedOrdersChunks) {
                setInfoModalData({
                    type: "loading",
                    msg:
                        "Generando los pickings para el grupo " +
                        chunkNumber +
                        " de " +
                        selectedOrdersChunks.length,
                    closeButton: false,
                });

                await handlePrintPickingsForChunk(selectedOrdersChunk);
                chunkNumber++;
            }

            setInfoModalData(null);
        } catch (error) {
            console.error(error);
            setInfoModalData({
                type: "error",
                msg: "No pude generar las etiquetas.",
                closeButton: true,
            });
        } finally {
            setPrintingPicking(false);
            fetchOrders();
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders, orderFilters]);

    useEffect(() => {
        const cleanSelectedOrders = () => {
            // Revisar que todo lo que está en selectedOrders exista
        };

        cleanSelectedOrders();
    }, [orders]);

    useEffect(() => {
        setItemQuantity(orders.reduce((a,b) => a + b.items.reduce((x,y) => x + y.quantity, 0), 0));
    }, [orders]);

    return (
        <div>
            <SubHeader>
                <Button
                    className="float-end ms-2 mt-1 mb-2"
                    onClick={() => fetchOrders()}
                >
                    <FaSync style={{ marginBottom: "3px" }} />
                </Button>
                <Button
                    variant="primary"
                    className="float-end ms-2 mt-1 mb-2"
                    disabled={!selectedOrders.length || printingPicking}
                    onClick={() => handlePrintPickings()}
                >
                    Descargar etiquetas para picking
                    {printingPicking ? (
                        <Fragment>
                            <Spinner
                                className="ms-2"
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="visually-hidden">Cargando...</span>
                        </Fragment>
                    ) : (
                        <Badge className="ms-2" bg="light" text="dark">
                            {selectedOrders.length}
                        </Badge>
                    )}
                </Button>
                <Button
                    variant="dark"
                    className="float-end mt-1 mb-2"
                    onClick={() => setBatchAssignation(!batchAssignation)}
                >
                    Picking masivo
                </Button>
                <h2>
                    VENTAS PARA PICKING
                    <span className="quantities">
                        <Badge className="ms-3" bg="dark">
                            {orders.length} ventas
                        </Badge>
                        <Badge className="ms-2" bg="info">
                            {itemQuantity} ítems
                        </Badge>
                    </span>
                </h2>
                Última actualización: {lastLoadTime.toLocaleString("es-CL")}
            </SubHeader>
            <br />

            <Container fluid>
                {infoModalData ? (
                    <InfoModal
                        closeButton={infoModalData.closeButton}
                        type={infoModalData.type}
                        size={infoModalData.size}
                        msg={infoModalData.msg}
                        setMsg={setInfoModalData}
                    />
                ) : null}

                <BatchAssignationsModal
                    batchAssignation={batchAssignation}
                    setBatchAssignation={setBatchAssignation}
                />

                <OrdersTable
                    orders={items}
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    selectedOrders={selectedOrders}
                    handleOrderSelect={handleOrderSelect}
                    orderFilters={orderFilters}
                    setOrderFilters={setOrderFilters}
                    ordersLoading={ordersLoading}
                    setInfoModalData={setInfoModalData}
                    customFields={orderFields}
                />
            </Container>
        </div>
    );
};

export default Pickings;
